import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Crown } from "../site/Logo";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.15, delay, ease }}>
      {children}
    </motion.span>
  </span>
);

const RotatingBadge = () => (
  <div data-testid="hero-rotating-badge" className="absolute -left-6 bottom-16 z-20 grid h-32 w-32 place-items-center rounded-full bg-plum shadow-2xl sm:-left-10 sm:h-36 sm:w-36">
    <svg viewBox="0 0 100 100" className="absolute inset-0 animate-spin-slow">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text fill="#F3E5AB" fontSize="8.4" letterSpacing="2.6" fontFamily="Jost" fontWeight="600">
        <textPath href="#circ">MORE THAN A STORE • IT'S A LIFESTYLE •</textPath>
      </text>
    </svg>
    <Crown className="h-8 w-12" />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const ghostX = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-cream grain">
      <div className="pointer-events-none absolute -right-40 top-10 h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(242,90,31,.35),rgba(214,31,105,.18)_45%,transparent_70%)] blur-2xl" />
      <motion.p style={{ x: ghostX }} aria-hidden="true" className="pointer-events-none absolute -bottom-10 left-0 whitespace-nowrap font-display text-[22vw] font-black leading-none text-plum/[0.045]">
        LUXE DREAM LUXE DREAM
      </motion.p>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 pb-16 pt-10 lg:grid-cols-[1.2fr_1fr] lg:gap-6 lg:pb-24 lg:pt-14">
        <div className="relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="eyebrow mb-6 flex items-center gap-3 text-magenta">
            The New Season Edit
          </motion.p>
          <h1 data-testid="hero-heading" className="text-plum">
            <Line delay={0.15} className="font-display text-5xl sm:text-7xl lg:text-[5.6rem] font-semibold uppercase leading-[0.95] tracking-tight">Your</Line>
            <Line delay={0.3} className="font-script whitespace-nowrap text-[clamp(3.4rem,15vw,7.4rem)] leading-[1.15] dream-gradient -my-3 pr-8">Dream Style</Line>
            <Line delay={0.45} className="font-display text-5xl sm:text-7xl lg:text-[5.6rem] font-semibold uppercase leading-[0.95] tracking-tight">Starts <em className="font-normal">Here</em></Line>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.9, ease }} className="mt-8 max-w-md text-base sm:text-lg leading-relaxed text-plum/80">
            Fashion, fabulous finds and little luxuries worth discovering.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease }} className="mt-9 flex flex-wrap gap-3">
            <Link to="/shop" data-testid="hero-shop-now-button" className="btn-luxe group">
              Shop Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/shop?tag=new" data-testid="hero-new-arrivals-button" className="btn-ghost">New Arrivals</Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.2, ease }}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          className="relative mx-auto w-full max-w-[460px] [perspective:1200px]"
        >
          <motion.div style={{ rotateX: rotX, rotateY: rotY }} className="relative [transform-style:preserve-3d]">
            <div className="absolute -inset-3 rounded-t-[999px] rounded-b-[2.5rem] border border-gold/60" style={{ transform: "translateZ(-40px)" }} />
            <div data-testid="hero-image-frame" className="relative aspect-[3/4] overflow-hidden rounded-t-[999px] rounded-b-[2.5rem] bg-plum shadow-[0_40px_80px_-30px_rgba(42,15,46,.55)]">
              <motion.img style={{ y: imgY, scale: 1.18 }} src="/images/hero.jpg" alt="Woman in a magenta blazer carrying Luxe Dream Market shopping bags" className="h-full w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-plum/50 via-transparent to-transparent" />
            </div>
            <motion.div
              style={{ transform: "translateZ(60px)" }}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2, duration: 0.9, ease }}
              className="absolute -right-3 top-1/3 z-20 w-44 rounded-2xl bg-white/85 p-3 shadow-xl backdrop-blur-xl sm:-right-10"
            >
              <Link to="/product/rose-plaid-shacket" data-testid="hero-featured-product" className="flex items-center gap-3">
                <img src="/images/rose-plaid-shacket.jpg" alt="Rose Plaid Dream Shacket" className="h-14 w-12 rounded-lg object-cover" />
                <div>
                  <p className="eyebrow text-[9px] text-magenta">Trending</p>
                  <p className="font-display text-sm leading-tight">Rose Plaid Shacket</p>
                  <p className="text-xs font-semibold">$54.00</p>
                </div>
              </Link>
            </motion.div>
            <RotatingBadge />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
